import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { toast } from "sonner";
import { MousePointerClick, ArrowUpCircle, Rocket, ShoppingBag, Trophy, Volume2, VolumeX } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const Ctx = createContext(null);
export const useClicker = () => useContext(Ctx);
export const ckErr = (e) => formatApiError(e?.response?.data?.detail) || "Something went wrong";
export const fmtCoins = (n) => {
  const v = Number(n || 0);
  if (v >= 1e9) return (v / 1e9).toFixed(2) + "B";
  if (v >= 1e6) return (v / 1e6).toFixed(2) + "M";
  if (v >= 1e4) return (v / 1e3).toFixed(1) + "K";
  return Math.floor(v).toLocaleString();
};

let _ac = null;
export function blip(muted, freq = 660, dur = 0.06, type = "triangle") {
  if (muted) return;
  try {
    _ac = _ac || new (window.AudioContext || window.webkitAudioContext)();
    if (_ac.state === "suspended") _ac.resume();
    const o = _ac.createOscillator(); const g = _ac.createGain();
    o.type = type; o.frequency.value = freq;
    g.gain.setValueAtTime(0.12, _ac.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, _ac.currentTime + dur);
    o.connect(g).connect(_ac.destination); o.start(); o.stop(_ac.currentTime + dur);
  } catch {}
}

function useClickerEngine(enabled) {
  const [state, setState] = useState(null);
  const [config, setConfig] = useState(null);
  const [muted, setMuted] = useState(() => localStorage.getItem("ck_muted") === "1");
  const pending = useRef(0);
  const flushing = useRef(false);

  const apply = useCallback((d) => { if (d?.state) setState(d.state); return d; }, []);

  const flush = useCallback(async () => {
    if (flushing.current) return;
    const taps = pending.current;
    flushing.current = true;
    pending.current = 0;
    try { apply((await api.post("/clicker/sync", { taps })).data); }
    catch (e) { pending.current += taps; if (e?.response?.status === 403) toast.error(ckErr(e)); }
    flushing.current = false;
  }, [apply]);

  const action = useCallback(async (path, body) => {
    await flush();
    try {
      const d = apply((await api.post(path, body || {})).data);
      return d;
    } catch (e) { toast.error(ckErr(e)); return null; }
  }, [apply, flush]);

  useEffect(() => {
    if (!enabled) return;
    api.get("/clicker/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/clicker/state").then((r) => apply(r.data)).catch((e) => toast.error(ckErr(e)));
    const id = setInterval(flush, 2000);
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onHide);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onHide); flush(); };
  }, [enabled, apply, flush]);

  const tap = useCallback(() => {
    pending.current += 1;
    setState((s) => {
      if (!s) return s;
      const b = s.booster && new Date(s.booster.until) > new Date() ? s.booster.mult : 1;
      const gain = s.click_value * b;
      return { ...s, coins: s.coins + gain, total_earned: s.total_earned + gain, taps_total: s.taps_total + 1 };
    });
  }, []);

  const toggleMute = () => setMuted((m) => { localStorage.setItem("ck_muted", m ? "0" : "1"); return !m; });

  // Display-only ticker for passive income + auto taps; server is the source of truth on next sync.
  useEffect(() => {
    if (!state) return;
    const id = setInterval(() => setState((s) => {
      if (!s) return s;
      const auto = s.auto && new Date(s.auto.until) > new Date() ? s.auto.tps * s.click_value : 0;
      const add = (s.passive_rate || 0) + auto;
      return add ? { ...s, coins: s.coins + add, total_earned: s.total_earned + add } : s;
    }), 1000);
    return () => clearInterval(id);
  }, [state ? 1 : 0]); // eslint-disable-line react-hooks/exhaustive-deps

  return { state, config, tap, action, flush, muted, toggleMute, reload: () => api.get("/clicker/state").then((r) => apply(r.data)) };
}

export default function ClickerLayout() {
  const { user, loading } = useAuth();
  const engine = useClickerEngine(!!user);
  useEffect(() => { document.title = "Jixxeo Clicker"; }, []);

  return (
    <div className="min-h-screen text-white flex flex-col" data-testid="clicker-layout"
      style={{ background: "radial-gradient(110% 70% at 50% 0%, #1b0b3a 0%, #070312 55%, #000 100%)", fontFamily: "'Outfit',ui-sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/40 border-b border-[#FF2BD6]/20">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/browse" className="text-[10px] mono uppercase tracking-widest text-white/50 hover:text-white" data-testid="clicker-back">← Apps</Link>
          <div className="flex-1 text-center font-black text-lg leading-none">
            Jixxeo <span className="bg-gradient-to-r from-[#00F0FF] via-[#FF2BD6] to-[#FFE300] bg-clip-text text-transparent">Clicker</span>
          </div>
          <button onClick={engine.toggleMute} data-testid="clicker-mute-btn" aria-label="Toggle sound"
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
            {engine.muted ? <VolumeX className="w-4 h-4 text-white/50" /> : <Volume2 className="w-4 h-4 text-[#00F0FF]" />}
          </button>
        </div>
      </header>
      <main className="flex-1 pb-24">
        <div className="max-w-xl mx-auto px-4 py-4">
          {!user && !loading ? (
            <div className="text-center py-20" data-testid="clicker-login-gate">
              <MousePointerClick className="w-14 h-14 mx-auto text-[#FF2BD6]" />
              <h1 className="font-black text-3xl mt-4">Tap your way to the top</h1>
              <p className="text-white/60 mt-2 text-sm">Sign in with your Jixxeo account so your coins, upgrades and level are saved.</p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/register?next=/clicker" data-testid="clicker-signup-btn" className="bg-[#FF2BD6] hover:bg-white text-black font-black px-6 py-3 rounded-full transition-colors">Create free account</Link>
                <Link to="/login?next=/clicker" data-testid="clicker-login-btn" className="bg-white/10 hover:bg-white/20 font-black px-6 py-3 rounded-full transition-colors">Sign in</Link>
              </div>
            </div>
          ) : !engine.state ? (
            <div className="text-center py-20 text-[#FF2BD6] animate-pulse mono text-sm">Loading your save…</div>
          ) : (
            <Ctx.Provider value={engine}><Outlet /></Ctx.Provider>
          )}
        </div>
      </main>
      <nav className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-black/70 border-t border-[#FF2BD6]/20">
        <div className="max-w-xl mx-auto grid grid-cols-5">
          <Tab to="/clicker" end icon={<MousePointerClick className="w-5 h-5" />} label="Tap" testId="clicker-tab-play" />
          <Tab to="/clicker/upgrades" icon={<ArrowUpCircle className="w-5 h-5" />} label="Upgrades" testId="clicker-tab-upgrades" />
          <Tab to="/clicker/boost" icon={<Rocket className="w-5 h-5" />} label="Boost" testId="clicker-tab-boost" />
          <Tab to="/clicker/store" icon={<ShoppingBag className="w-5 h-5" />} label="Store" testId="clicker-tab-store" />
          <Tab to="/clicker/ranks" icon={<Trophy className="w-5 h-5" />} label="Ranks" testId="clicker-tab-ranks" />
        </div>
      </nav>
    </div>
  );
}

function Tab({ to, icon, label, testId, end }) {
  return (
    <NavLink to={to} end={end} data-testid={testId}
      className={({ isActive }) => "flex flex-col items-center gap-0.5 py-2.5 text-[10px] uppercase tracking-widest transition-colors " +
        (isActive ? "text-[#FF2BD6]" : "text-white/50 hover:text-white/80")}>
      {icon}<span className="font-bold">{label}</span>
    </NavLink>
  );
}

export function CoinBar() {
  const { state } = useClicker();
  return (
    <div className="flex items-center justify-between rounded-2xl bg-white/5 border border-white/10 px-4 py-3" data-testid="clicker-coin-bar">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40">Coins</div>
        <div className="font-black text-2xl text-[#FFE300]" data-testid="clicker-coins">{fmtCoins(state.coins)}</div>
      </div>
      <div className="text-right text-[11px] text-white/60">
        <div>{fmtCoins(state.click_value)} / tap</div>
        <div>{fmtCoins(state.passive_rate)} / sec</div>
      </div>
    </div>
  );
}
